/**
 * NetworkDirectoryPage — Browse & discover other companies on Boox Network
 * Route: /network/directory
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Search, Globe, Building2, UserPlus, CheckCircle2,
  Clock, RefreshCw, MapPin, Tags, MessageSquare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  networkDirectoryApi, networkConnectApi,
  type NetworkMember,
} from '@/lib/network-api';
import { cn } from '@/lib/utils';

// ─── Member card ──────────────────────────────────────────────────────────────

function MemberCard({
  member,
  onConnect,
  onDm,
}: {
  member: NetworkMember;
  onConnect: (m: NetworkMember) => void;
  onDm: (m: NetworkMember) => void;
}) {
  const isConnected = member.connection_status === 'active';
  const isPending   = member.connection_status === 'pending_sent';

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4 transition-all hover:border-primary/30 hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span className="truncate font-semibold text-foreground">{member.company_name}</span>
            {member.verification_status === 'fully_verified' && (
              <CheckCircle2 className="size-4 shrink-0 text-green-500" />
            )}
          </div>
          {member.tagline && (
            <span className="truncate text-xs text-muted-foreground">{member.tagline}</span>
          )}
        </div>
        <div className="shrink-0 flex items-center gap-1.5">
          {isConnected ? (
            <>
              <Button size="sm" variant="outline" onClick={() => onDm(member)}>
                <MessageSquare className="mr-1.5 size-3.5" />Message
              </Button>
              <Badge variant="outline" className="border-green-500 text-green-600">
                <CheckCircle2 className="mr-1 size-3" />Connected
              </Badge>
            </>
          ) : isPending ? (
            <Badge variant="outline" className="text-muted-foreground">
              <Clock className="mr-1 size-3" />Pending
            </Badge>
          ) : (
            <Button size="sm" variant="outline" onClick={() => onConnect(member)}>
              <UserPlus className="mr-1.5 size-3.5" />Connect
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {member.country_code && (
          <Badge variant="secondary" className="gap-1 text-xs">
            <MapPin className="size-3" />{member.country_code}
          </Badge>
        )}
        {member.industry && (
          <Badge variant="secondary" className="text-xs">{member.industry}</Badge>
        )}
        {member.capabilities.slice(0, 3).map((c) => (
          <Badge key={c} variant="outline" className="text-xs">{c}</Badge>
        ))}
        {member.capabilities.length > 3 && (
          <Badge variant="outline" className="text-xs text-muted-foreground">
            +{member.capabilities.length - 3}
          </Badge>
        )}
      </div>

      {member.service_types.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {member.service_types.slice(0, 4).map((s) => (
            <span key={s} className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">{s}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Connect dialog ───────────────────────────────────────────────────────────

function ConnectDialog({
  target,
  onClose,
  onSent,
}: {
  target: NetworkMember | null;
  onClose: () => void;
  onSent: (memberId: string) => void;
}) {
  const [connectionType, setConnectionType] = useState<string>('partner');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSend() {
    if (!target) return;
    setLoading(true);
    try {
      await networkConnectApi.send({
        to_member_id: target.id,
        connection_type: connectionType,
        introduction_message: message || undefined,
      });
      toast.success(`Connection request sent to ${target.company_name}`);
      onSent(target.id);
      onClose();
    } catch {
      toast.error('Failed to send request, please try again');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={!!target} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Connect with {target?.company_name}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label>Connection Type</Label>
            <Select value={connectionType} onValueChange={setConnectionType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="partner">Partner</SelectItem>
                <SelectItem value="vendor">Vendor</SelectItem>
                <SelectItem value="customer">Customer</SelectItem>
                <SelectItem value="agent">Agent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Introduction <span className="text-muted-foreground">(optional, max 500 characters)</span></Label>
            <Textarea
              rows={3}
              maxLength={500}
              placeholder="Introduce your company and collaboration intent…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSend} disabled={loading}>
            {loading ? 'Sending…' : 'Send Request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function NetworkDirectoryPage() {
  const navigate = useNavigate();
  const [members, setMembers]   = useState<NetworkMember[]>([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(false);
  const [q, setQ]               = useState('');
  const [region, setRegion]     = useState('');
  const [connectTarget, setConnectTarget] = useState<NetworkMember | null>(null);

  const load = useCallback(async (search: string, reg: string, p: number) => {
    setLoading(true);
    try {
      const res = await networkDirectoryApi.list({
        q: search || undefined,
        region: reg || undefined,
        page: p,
        limit: 20,
      });
      setMembers(p === 1 ? res.items : (prev) => [...prev, ...res.items]);
      setTotal(res.total);
    } catch {
      toast.error('Failed to load directory');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    load(q, region, 1);
  }, [q, region, load]);

  function handleConnectSent(memberId: string) {
    setMembers((prev) =>
      prev.map((m) => m.id === memberId ? { ...m, connection_status: 'pending_sent' } : m),
    );
  }

  const hasMore = members.length < total;

  return (
    <div className="flex h-full flex-col gap-0">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <div>
          <h1 className="text-lg font-semibold">Boox Network Directory</h1>
          <p className="text-sm text-muted-foreground">Discover and connect with partners in the logistics ecosystem</p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/network/connections')}>
          My Connections
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 border-b border-border px-6 py-3">
        <div className="relative flex-1" style={{ minWidth: 200 }}>
          <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search companies, services…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <Input
          className="w-32"
          placeholder="Region (SG, MY…)"
          value={region}
          onChange={(e) => setRegion(e.target.value.toUpperCase())}
          maxLength={5}
        />
        <Button variant="ghost" size="icon" onClick={() => load(q, region, 1)}>
          <RefreshCw className="size-4" />
        </Button>
      </div>

      {/* Stats bar */}
      {!loading && total > 0 && (
        <div className="flex items-center gap-2 px-6 py-2 text-sm text-muted-foreground">
          <Globe className="size-3.5" />
          <span>{total} companies</span>
        </div>
      )}

      {/* Grid */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {loading && members.length === 0 ? (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-36 rounded-lg" />
            ))}
          </div>
        ) : members.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-20 text-center text-muted-foreground">
            <Building2 className="size-10 opacity-30" />
            <p>No companies match your search</p>
          </div>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {members.map((m) => (
                <MemberCard key={m.id} member={m} onConnect={setConnectTarget} onDm={(m) => navigate(`/network/messages/${m.id}`)} />
              ))}
            </div>
            {hasMore && (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="outline"
                  disabled={loading}
                  onClick={() => {
                    const next = page + 1;
                    setPage(next);
                    load(q, region, next);
                  }}
                >
                  {loading ? 'Loading…' : 'Load More'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <ConnectDialog
        target={connectTarget}
        onClose={() => setConnectTarget(null)}
        onSent={handleConnectSent}
      />
    </div>
  );
}
